import { useState } from "react";
import { useFormContext } from "react-hook-form";

import { FORM_MODES } from "routes/revenue_opportunity/form/defaults";
import { FORM_ORIGINS } from "routes/revenue_opportunity/paths";
import CaseService from "services/Case";
import ContactService from "services/Contact";

const ENTITY = "entity_id";
const CONTACT = "contact_id";
const CAMPAIGN = "campaign_id";
const CASES = "case_ids";

const RELATION_DEFAULTS = {
  [ENTITY]: null,
  [CONTACT]: null,
  [CAMPAIGN]: null,
  [CASES]: [],
};

const MAIN_BY_ORIGIN = {
  [FORM_ORIGINS.entity]: ENTITY,
  [FORM_ORIGINS.contact]: CONTACT,
  [FORM_ORIGINS.case]: CASES,
  [FORM_ORIGINS.campaign]: CAMPAIGN,
  [FORM_ORIGINS.campaignTarget]: CAMPAIGN,
};

const CAMPAIGN_ORIGINS = [FORM_ORIGINS.campaign, FORM_ORIGINS.campaignTarget];

const initialMain = (mode, origin) =>
  mode === FORM_MODES.create ? (MAIN_BY_ORIGIN[origin] ?? null) : ENTITY;

// The legacy form split the relations into a "main" block, whose changes reset
// the others, and a secondary block, whose changes do not. A field belongs to
// the main block depending on which relation was picked first.
const isMainField = (field, main) => {
  if (!main) return true;
  if (field === ENTITY) return main !== CAMPAIGN && main !== CONTACT;
  if (field === CONTACT) return main !== ENTITY;
  return field === main;
};

const isCleared = value =>
  value == null || (Array.isArray(value) && value.length === 0);

/**
 * The relation rules of the Revenue Opportunity form: which relation fields
 * are read-only, which change clears which, and when the entity follows a
 * picked case or contact.
 */
export const useRelationRules = ({ mode, origin, recordCaseIds = [] }) => {
  const { setValue } = useFormContext();
  const [mainField, setMainField] = useState(() => initialMain(mode, origin));
  const [derivedEntity, setDerivedEntity] = useState(null);

  const isCampaignOrigin = CAMPAIGN_ORIGINS.includes(origin);

  const setRelation = (field, value) =>
    setValue(field, value, { shouldDirty: true, shouldValidate: true });

  const deriveEntity = async load => {
    const derived = await load();
    if (derived.id == null) return;
    setDerivedEntity(derived);
    setRelation(ENTITY, derived.id);
  };

  const clearAfterMainChange = field => {
    if (field === ENTITY) {
      setRelation(CAMPAIGN, null);
      setRelation(CASES, []);
    }
    if (field === CAMPAIGN) {
      if (!isCampaignOrigin) setRelation(ENTITY, null);
      if (recordCaseIds.length === 0) setRelation(CASES, []);
    }
  };

  const onRelationChange = (field, value) => {
    let nextMain = mainField;
    if (isMainField(field, mainField)) {
      if (isCleared(value)) {
        setMainField(null);
        Object.entries(RELATION_DEFAULTS).forEach(([name, empty]) =>
          setRelation(name, empty)
        );
        return;
      }
      nextMain = field;
      setMainField(field);
      clearAfterMainChange(field);
    }

    if (field === CONTACT && nextMain === CONTACT && value) {
      deriveEntity(async () => {
        const contact = await ContactService.getContact(value);
        return { id: contact.entity_id, label: contact.entity_name };
      });
    }
    if (field === CASES && nextMain === CASES && value[0]) {
      deriveEntity(async () => {
        const caseData = await CaseService.get(value[0]);
        return { id: caseData.entity_id, label: caseData.entity?.name };
      });
    }
  };

  return {
    onRelationChange,
    derivedEntity,
    readOnly: {
      [ENTITY]: mainField === CASES || origin === FORM_ORIGINS.entity,
      [CONTACT]: origin === FORM_ORIGINS.contact,
      [CAMPAIGN]: isCampaignOrigin,
      [CASES]: origin === FORM_ORIGINS.case,
    },
  };
};
